import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../../api";
import "./pos.css";

/*
 * Point of Sale – React port of enhanced_pos_full.php
 * API: GET /api/pos/bootstrap, GET /api/pos/lookup?code=, POST /api/pos/sale  (backend/pos.py)
 *
 * Keyboard: F2 complete sale · F3 preview receipt · / focus search · Esc cancel auto-pay
 *           Delete removes the selected cart line · a barcode scanner works from anywhere on the page
 */

const SHOP_NAME = "AE2C MINI MARKET";
const SHOP_TAGLINE = "Super Quality Everyday Needs";
const AUTO_PAY_SECONDS = 3;   // completes the sale this many seconds after enough cash is entered; 0 turns it off
const MAX_CARDS = 200;        // cards drawn at once; keep typing to narrow larger catalogs
const PAYMENT_METHODS = [
  { value: "Cash", label: "Cash" },
  { value: "LipaNumber", label: "Lipa" },
  { value: "Bank", label: "Bank" },
];

/* ───────── helpers ───────── */
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmt = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const norm = (s) => String(s || "").replace(/[^A-Za-z0-9]/g, "").toLowerCase();
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const lineTotal = (i) => round2(i.price * i.qty * (1 - (Number(i.discount) || 0) / 100));

function matches(products, raw) {
  const q = raw.trim().toLowerCase();
  if (!q) return products;
  const qn = norm(raw);
  return products.filter((p) =>
    (p.name || "").toLowerCase().includes(q) ||
    (p.manufacturer || "").toLowerCase().includes(q) ||
    (qn && norm(p.barcode).includes(qn)) ||
    String(p.id) === q
  );
}

/** Find a product for a scanned/typed code in memory: exact, normalised, leading-zero-insensitive, then id. */
function findLocal(products, code) {
  const cn = norm(code);
  let p = products.find((x) => x.barcode && String(x.barcode).trim() === code);
  if (!p && cn) p = products.find((x) => x.barcode && norm(x.barcode) === cn);
  if (!p && /^\d+$/.test(cn)) {
    const numeric = String(parseInt(cn, 10));
    p = products.find((x) => {
      const b = String(x.barcode || "").replace(/\D/g, "");
      return b && String(parseInt(b, 10)) === numeric;
    });
  }
  if (!p && /^\d+$/.test(code)) p = products.find((x) => x.id === parseInt(code, 10));
  return p || null;
}

/** Receipt as a standalone HTML document (used for both preview and the final receipt). */
function receiptHtml({ client, date, lines, subtotal, discount, total, received, change, preview }) {
  const rows = lines.map((l) => `
    <tr><td>${esc(l.name)}</td><td class="c">${l.qty}</td><td class="r">${fmt(l.price)}</td>
    <td class="r">${fmt(l.discount)}%</td><td class="r">${fmt(l.total)}</td></tr>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title><style>
    body{font-family:'Segoe UI',Arial,sans-serif;font-size:12px;color:#111;margin:0;padding:12px;width:330px}
    h3{text-align:center;margin:0;font-size:15px}.tag{text-align:center;font-size:11px;color:#444;margin-bottom:8px}
    table{width:100%;border-collapse:collapse;margin-top:8px}th{border-bottom:1px solid #333;padding:3px 0;text-align:left}
    td{padding:5px 0;border-bottom:1px dashed #bbb}.r{text-align:right}.c{text-align:center}
    tfoot td{border:0;padding:3px 0}.g td{font-weight:bold;border-top:1px solid #333}
    .end{text-align:center;margin-top:10px;font-size:11px}</style></head><body>
    <h3>${SHOP_NAME}</h3><div class="tag">${SHOP_TAGLINE}</div>
    ${client ? `<div>Client: ${esc(client)}</div>` : ""}<div>Date: ${esc(date)}</div>
    <table><thead><tr><th>Item</th><th class="c">Qty</th><th class="r">Unit</th><th class="r">Disc</th><th class="r">Total</th></tr></thead>
    <tbody>${rows}</tbody><tfoot>
    ${discount > 0 ? `<tr><td colspan="4" class="r">Subtotal</td><td class="r">${fmt(subtotal)}</td></tr>
    <tr><td colspan="4" class="r">Discount</td><td class="r">-${fmt(discount)}</td></tr>` : ""}
    <tr class="g"><td colspan="4" class="r">Total</td><td class="r">${fmt(total)}</td></tr>
    ${received != null ? `<tr><td colspan="4" class="r">Received</td><td class="r">${fmt(received)}</td></tr>
    <tr><td colspan="4" class="r">Change</td><td class="r">${fmt(change)}</td></tr>` : ""}
    </tfoot></table>
    <div class="end">${preview ? "*** PREVIEW - NOT A RECEIPT ***" : "Thank you for shopping with us!"}</div></body></html>`;
}

/** Print through a hidden iframe: no popup window, so popup blockers can't stop the receipt. */
function printHtml(html) {
  const frame = document.createElement("iframe");
  Object.assign(frame.style, { position: "fixed", right: 0, bottom: 0, width: 0, height: 0, border: 0 });
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  }, 250);
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <span className="mono">{now.toLocaleTimeString("en-GB")}</span>;
}

/* ───────── page ───────── */
export default function PointOfSale() {
  const api = useApi();
  const searchRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [cart, setCart] = useState([]);
  const [selected, setSelected] = useState(null);
  const [clientId, setClientId] = useState(0);
  const [method, setMethod] = useState("Cash");
  const [received, setReceived] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);       // { tone: "ok"|"err", text }
  const [flash, setFlash] = useState(null);         // { tone: "ok"|"err", text? }
  const [countdown, setCountdown] = useState(null);
  const [autoPayCancelled, setAutoPayCancelled] = useState(false);

  /* ── load products & clients ── */
  const loadData = useCallback(async (keepClient = false) => {
    try {
      const d = await api.get("/api/pos/bootstrap");
      setProducts(d.products || []);
      setClients(d.clients || []);
      if (!keepClient) setClientId(d.clients?.[0]?.id || 0);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (!loading) searchRef.current?.focus(); }, [loading]);

  /* ── derived ── */
  const filtered = useMemo(() => matches(products, deferredSearch), [products, deferredSearch]);
  const subtotal = round2(cart.reduce((s, i) => s + i.price * i.qty, 0));
  const total = round2(cart.reduce((s, i) => s + lineTotal(i), 0));
  const discountTotal = round2(subtotal - total);
  const receivedNum = parseFloat(received) || 0;
  const canPay = cart.length > 0 && total > 0 && receivedNum >= total && clientId > 0 && !submitting;

  /* ── feedback ── */
  const flashError = useCallback((text) => {
    setFlash({ tone: "err", text });
    setTimeout(() => setFlash((f) => (f?.text === text ? null : f)), 2200);
  }, []);
  const flashOk = useCallback(() => {
    setFlash({ tone: "ok" });
    setTimeout(() => setFlash((f) => (f?.tone === "ok" ? null : f)), 600);
  }, []);

  /* ── cart operations ── */
  // cartRef lets addToCart see the latest cart immediately, even for two scans in a row
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const addToCart = useCallback((p) => {
    const c = cartRef.current;
    const existing = c.find((i) => i.id === p.id);
    let next;
    if (existing) {
      if (existing.qty + 1 > p.stock) { flashError(`Stock limit reached for ${p.name}`); return false; }
      next = c.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1, stock: p.stock } : i));
    } else {
      if (p.stock <= 0) { flashError(`${p.name} is out of stock`); return false; }
      next = [...c, { id: p.id, name: p.name, price: Number(p.price), qty: 1, discount: 0, stock: p.stock }];
    }
    cartRef.current = next;
    setCart(next);
    return true;
  }, [flashError]);

  const changeQty = (idx, delta) => {
    const it = cart[idx];
    if (delta > 0 && it.qty + 1 > it.stock) { flashError("Stock limit reached"); return; }
    if (delta < 0 && it.qty <= 1) { removeItem(idx); return; }
    setCart((c) => c.map((i, k) => (k === idx ? { ...i, qty: i.qty + delta } : i)));
  };
  const removeItem = useCallback((idx) => {
    setCart((c) => c.filter((_, k) => k !== idx));
    setSelected(null);
  }, []);
  const setDiscount = (idx, value) => setCart((c) => c.map((i, k) => (k === idx ? { ...i, discount: value } : i)));
  const sanitizeDiscount = (idx) => setCart((c) => c.map((i, k) => {
    if (k !== idx) return i;
    const d = Math.min(100, Math.max(0, Number(i.discount) || 0));
    return { ...i, discount: d };
  }));
  const clearCart = () => {
    if (!cart.length || !window.confirm("Clear cart?")) return;
    setCart([]); setSelected(null); setReceived("");
  };

  /* ── scanning ── */
  const handleScan = useCallback(async (raw) => {
    const code = String(raw || "").trim();
    if (!code) return;
    let p = findLocal(products, code);
    if (!p) {
      try {
        const d = await api.get("/api/pos/lookup", { code });
        if (d.ok && d.product) {
          p = d.product;
          setProducts((list) => (list.some((x) => x.id === p.id) ? list : [...list, p]));
        }
      } catch { /* reported below */ }
    }
    setSearch("");
    if (!p) { flashError(`Not found: ${code}`); return; }
    if (addToCart(p)) flashOk();
    searchRef.current?.focus();
  }, [products, api, addToCart, flashError, flashOk]);

  const onSearchEnter = () => {
    const visible = matches(products, search);
    if (search.trim() && visible.length === 1) {
      if (addToCart(visible[0])) flashOk();
      setSearch("");
      return;
    }
    handleScan(search);
  };

  /* ── preview & sale ── */
  const cartLines = () => cart.map((i) => ({ name: i.name, qty: i.qty, price: i.price, discount: Number(i.discount) || 0, total: lineTotal(i) }));

  const openPreview = () => {
    if (!cart.length) { flashError("Cart is empty"); return; }
    printHtml(receiptHtml({
      date: new Date().toLocaleString("en-GB"), lines: cartLines(),
      subtotal, discount: discountTotal, total, received: null, preview: true,
    }));
  };

  const completeSale = async () => {
    if (submitting) return;
    if (!cart.length) { flashError("Cart is empty"); return; }
    if (!clientId) { flashError("Select a client"); return; }
    if (receivedNum < total) { flashError("Received amount is less than total"); return; }

    setSubmitting(true);
    setNotice(null);
    try {
      const { receipt } = await api.post("/api/pos/sale", {
        client_id: clientId,
        payment_method: method,
        received_amount: receivedNum,
        items: cart.map((i) => ({ id: i.id, qty: i.qty, discount: Number(i.discount) || 0 })),
      });
      const lines = receipt.lines.map((l) => ({ name: l.product_name, qty: l.quantity, price: l.unit_price, discount: l.discount, total: l.line_total }));
      const sub = round2(lines.reduce((s, l) => s + l.price * l.qty, 0));
      printHtml(receiptHtml({
        client: receipt.client, date: receipt.date, lines,
        subtotal: sub, discount: round2(sub - receipt.total), total: receipt.total,
        received: receipt.received, change: receipt.change,
      }));
      setNotice({ tone: "ok", text: `Sale completed. Change: ${fmt(receipt.change)}` });
      setCart([]); setSelected(null); setReceived("");
      loadData(true); // refresh stock levels
      searchRef.current?.focus();
    } catch (e) {
      setNotice({ tone: "err", text: `Sale not completed: ${e.message}` });
      setAutoPayCancelled(true); // don't retry automatically; it re-arms when the cart or amount changes
    } finally {
      setSubmitting(false);
    }
  };

  /* ── auto-pay countdown (re-arms whenever the cart or amount changes; Esc cancels) ── */
  useEffect(() => { setAutoPayCancelled(false); }, [cart, receivedNum]);
  const completeRef = useRef(completeSale);
  completeRef.current = completeSale;
  useEffect(() => {
    if (!AUTO_PAY_SECONDS || !canPay || autoPayCancelled) { setCountdown(null); return undefined; }
    let n = AUTO_PAY_SECONDS;
    setCountdown(n);
    const t = setInterval(() => {
      n -= 1;
      if (n <= 0) { clearInterval(t); setCountdown(null); completeRef.current(); }
      else setCountdown(n);
    }, 1000);
    return () => clearInterval(t);
  }, [canPay, autoPayCancelled, cart, receivedNum]);

  /* ── keyboard shortcuts & global scanner ── */
  const keys = useRef({});
  keys.current = { completeSale, openPreview, handleScan, removeItem, selected, countdown };
  useEffect(() => {
    let buffer = "";
    let lastKeyAt = 0;
    const onKey = (e) => {
      const k = keys.current;
      const tag = (e.target.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || tag === "select";

      if (e.key === "F2") { e.preventDefault(); k.completeSale(); return; }
      if (e.key === "F3") { e.preventDefault(); k.openPreview(); return; }
      if (e.key === "Escape") { if (k.countdown != null) setAutoPayCancelled(true); return; }
      if (e.key === "Delete" && !typing && k.selected != null) { e.preventDefault(); k.removeItem(k.selected); return; }
      if (e.key === "/" && !typing) { e.preventDefault(); searchRef.current?.focus(); return; }

      // Scanner: a fast burst of characters ending in Enter, while focus is NOT in a field.
      // (When the search box has focus it handles Enter itself, so the scan isn't added twice.)
      if (typing) { buffer = ""; return; }
      const now = Date.now();
      if (e.key === "Enter") {
        if (buffer.length >= 3 && now - lastKeyAt < 100) { e.preventDefault(); k.handleScan(buffer); }
        buffer = "";
        return;
      }
      if (e.key.length === 1) buffer = now - lastKeyAt < 80 ? buffer + e.key : e.key;
      lastKeyAt = now;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ───────── render ───────── */
  const shown = filtered.slice(0, MAX_CARDS);

  return (
    <main className="pos-page">
      <div className="pos-head">
        <div>
          <h1>Point of Sale</h1>
          <p>AE2C Mini Market · <Clock /></p>
        </div>
        <div className="keys" aria-label="Keyboard shortcuts">
          <span><kbd>F2</kbd>Pay</span><span><kbd>F3</kbd>Preview</span>
          <span><kbd>/</kbd>Search</span><span><kbd>Del</kbd>Remove line</span><span><kbd>Esc</kbd>Stop auto-pay</span>
        </div>
      </div>

      {notice && (
        <div className={`pos-notice ${notice.tone}`} role={notice.tone === "err" ? "alert" : "status"}>
          <span>{notice.text}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}><i className="fas fa-xmark" /></button>
        </div>
      )}
      {loadError && (
        <div className="pos-notice err" role="alert">
          <span>Couldn't load products: {loadError}</span>
          <button type="button" className="btn" onClick={() => { setLoading(true); loadData(); }}>Retry</button>
        </div>
      )}

      <div className="pos-grid">
        {/* ───── products ───── */}
        <section className="panel pos-products" aria-label="Products">
          <div className="pos-search">
            <div className="box">
              <i className="fas fa-magnifying-glass" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); onSearchEnter(); }
                  if (e.key === "Escape") setSearch("");
                }}
                placeholder="Search by name or manufacturer, or scan a barcode"
                aria-label="Search products or scan a barcode"
                className={flash?.tone === "ok" ? "flash-ok" : flash?.tone === "err" ? "flash-err" : ""}
                autoComplete="off"
              />
              {search && (
                <button type="button" className="clear" aria-label="Clear search"
                  onClick={() => { setSearch(""); searchRef.current?.focus(); }}>
                  <i className="fas fa-xmark" />
                </button>
              )}
            </div>
            <div className="meta">
              {flash?.tone === "err" ? (
                <span className="err" role="alert">{flash.text}</span>
              ) : search ? (
                <span>{filtered.length} {filtered.length === 1 ? "match" : "matches"}{filtered.length > MAX_CARDS ? `, showing the first ${MAX_CARDS}` : ""}</span>
              ) : (
                <span>Press <kbd>/</kbd> to search. Scan a barcode at any time.</span>
              )}
              <button type="button" className="linkbtn" onClick={clearCart} disabled={!cart.length}>Clear cart</button>
            </div>
          </div>

          <div className="pos-cards">
            {loading && <div className="pos-empty"><i className="fas fa-spinner fa-spin" />Loading products…</div>}
            {!loading && shown.map((p) => {
              const out = p.stock <= 0;
              return (
                <button key={p.id} type="button" className="pcard" disabled={out}
                  onClick={() => { addToCart(p); searchRef.current?.focus(); }}>
                  <span className="nm">{p.name}</span>
                  <span className="mf">{p.manufacturer || "No manufacturer"}</span>
                  <span className="row">
                    <span className="pr mono">{fmt(p.price)}</span>
                    <span className={`st ${out ? "out" : p.stock < 5 ? "low" : "ok"}`}>{out ? "Out of stock" : `${p.stock} in stock`}</span>
                  </span>
                </button>
              );
            })}
            {!loading && !loadError && filtered.length === 0 && (
              <div className="pos-empty">
                <i className="fas fa-magnifying-glass" />
                {search ? <>No products match “{search}”.<br /><small>Try a different name, manufacturer or barcode.</small></> : "No products available."}
              </div>
            )}
          </div>
        </section>

        {/* ───── cart ───── */}
        <section className="panel pos-cart" aria-label="Cart">
          <div className="hd">
            <h2>Cart</h2>
            <span className="pill">{cart.length} {cart.length === 1 ? "item" : "items"}</span>
          </div>

          <div className="pos-opts">
            <div>
              <label htmlFor="pos-client">Client</label>
              <select id="pos-client" value={clientId} onChange={(e) => setClientId(Number(e.target.value))}>
                {clients.length === 0 && <option value={0}>No clients</option>}
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label id="pos-method">Payment</label>
              <div className="seg" role="group" aria-labelledby="pos-method">
                {PAYMENT_METHODS.map((m) => (
                  <button key={m.value} type="button" aria-pressed={method === m.value} onClick={() => setMethod(m.value)}>{m.label}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="pos-lines">
            {cart.length === 0 ? (
              <div className="cart-empty"><i className="fas fa-cart-shopping" />Cart is empty.<br /><small>Click a product or scan a barcode.</small></div>
            ) : cart.map((item, idx) => (
              <div key={item.id} className={`pos-line ${selected === idx ? "sel" : ""}`} onClick={() => setSelected(idx)}>
                <div className="top">
                  <div style={{ minWidth: 0 }}>
                    <div className="nm">{item.name}</div>
                    <div className="sub mono">{fmt(item.price)} × {item.qty}</div>
                  </div>
                  <div>
                    <div className="lt mono">{fmt(lineTotal(item))}</div>
                    <button type="button" className="rm" onClick={(e) => { e.stopPropagation(); removeItem(idx); }}>Remove</button>
                  </div>
                </div>
                <div className="ctl">
                  <span className="qty">
                    <button type="button" aria-label={`One less ${item.name}`} onClick={(e) => { e.stopPropagation(); changeQty(idx, -1); }}>−</button>
                    <span className="mono" aria-live="polite">{item.qty}</span>
                    <button type="button" aria-label={`One more ${item.name}`} onClick={(e) => { e.stopPropagation(); changeQty(idx, 1); }}>+</button>
                  </span>
                  <label className="disc" onClick={(e) => e.stopPropagation()}>
                    Discount
                    <input type="number" min="0" max="100" step="0.5" value={item.discount}
                      onChange={(e) => setDiscount(idx, e.target.value)} onBlur={() => sanitizeDiscount(idx)} />
                    %
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="pos-pay">
            <div className="sums">
              <div><span>Subtotal</span><span className="mono">{fmt(subtotal)}</span></div>
              {discountTotal > 0 && <div><span>Discount</span><span className="mono neg">−{fmt(discountTotal)}</span></div>}
              <div className="grand"><span>Total</span><span className="mono">{fmt(total)}</span></div>
            </div>

            <div className="recv">
              <label htmlFor="pos-received">Amount received</label>
              <input id="pos-received" type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
                value={received} onChange={(e) => setReceived(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); completeSale(); } }} />
            </div>

            {cart.length > 0 && (receivedNum >= total && total > 0 ? (
              <div className="chg ok" role="status">
                <span>Change: <strong className="mono">{fmt(receivedNum - total)}</strong></span>
                {countdown != null && <small>Paying in {countdown}s · Esc to stop</small>}
              </div>
            ) : (
              <div className="chg short">Need <strong className="mono">{fmt(total - receivedNum)}</strong> more</div>
            ))}

            <div className="pos-actions">
              <button type="button" className="btn-pay" disabled={!canPay} onClick={completeSale}>
                {submitting ? "Completing…" : "Complete sale"} <kbd style={{ background: "rgba(255,255,255,.2)", color: "#fff" }}>F2</kbd>
              </button>
              <button type="button" className="btn" onClick={openPreview} title="Preview receipt (F3)">Preview</button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
